import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";
import Input from "@/components/ui/input";
import Tabs from "@/components/controls/Tabs";
import { getCurrencySymbol, getSymbolAdornmentPadding } from "../consts";

type DiscountType = "percentage" | "amount";

interface DiscountSectionProps {
  showDiscountInput: boolean;
  discount: number;
  discountType: DiscountType;
  total: number;
  onShowDiscount: () => void;
  onDiscountChange: (value: number) => void;
  onDiscountTypeChange: (type: DiscountType) => void;
  currency?: string | null;
}

const DiscountSection: React.FC<DiscountSectionProps> = ({
  showDiscountInput,
  discount,
  discountType,
  total,
  onShowDiscount,
  onDiscountChange,
  onDiscountTypeChange,
  currency = "USD"
}) => {
  const { t } = useTranslation();
  const currencySymbol = getCurrencySymbol(currency);
  const adornment = discountType === "percentage" ? "%" : currencySymbol;

  return (
    <div className="flex flex-col gap-2 items-start w-full">
      {!showDiscountInput ? (
        <Button onClick={onShowDiscount} variant="tertiary">
          {t("contractTerms.addDiscount")}
        </Button>
      ) : (
        <div className="flex flex-col gap-2 w-full">
          <Text variant="bodySecondary">{t("contractTerms.discount")}</Text>
          <div className="flex gap-2 items-center w-full">
            <div className="relative flex-1">
              <Input
                type="number"
                value={discount || ""}
                onChange={(e) => {
                  const value = Math.max(0, parseFloat(e.target.value) || 0);
                  onDiscountChange(
                    discountType === "percentage"
                      ? Math.min(value, 100)
                      : Math.min(value, Math.abs(total))
                  );
                }}
                min={0}
                max={discountType === "percentage" ? 100 : Math.abs(total)}
                step="any"
                placeholder={
                  discountType === "percentage"
                    ? t("contractTerms.addPercentageOff")
                    : t("contractTerms.addAmountOff")
                }
                className="w-full"
                style={{ paddingRight: getSymbolAdornmentPadding(adornment) }}
              />
              <Text
                as="span"
                variant="bodyTertiary"
                className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
              >
                {adornment}
              </Text>
            </div>
            <Tabs
              options={{
                percentage: t("contractTerms.percentage"),
                amount: t("contractTerms.amount")
              }}
              value={discountType}
              valueSelected={(value) => {
                onDiscountTypeChange(value as DiscountType);
                onDiscountChange(0);
              }}
              size="lg"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscountSection;
